import { useMemo } from 'react';
import {
  Dropdown,
  Field,
  Option,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { FIELD_LABEL_LAYOUT, useFetch } from '@moko/ui';
import { getTimezones, type ITimezone } from '@moko/utils';
import { useTranslation } from 'react-i18next';
import {
  toTenantTimezonePayload,
  type ITenantTimezone,
} from '../../services/tenantService';

export interface TenantTimezoneFieldsProps {
  value: ITenantTimezone[];
  onChange: (timezones: ITenantTimezone[]) => void;
  disabled?: boolean;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
    width: '100%',
    maxWidth: '520px',
    boxSizing: 'border-box',
  },
  field: {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  },
});

const TenantTimezoneFields = ({
  value,
  onChange,
  disabled = false,
}: TenantTimezoneFieldsProps) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const catalogFetch = useFetch<ITimezone[]>({
    queryKey: ['timezones'],
    queryFn: getTimezones,
  });

  const catalog = useMemo(
    () =>
      (catalogFetch.data ?? [])
        .map((timezone) => timezone.name)
        .filter((name) => name.trim() !== ''),
    [catalogFetch.data]
  );
  const catalogError = catalogFetch.error
    ? t('timezonesLoadFailed', 'Could not load timezones.')
    : undefined;
  const selectedIds = useMemo(
    () =>
      value.filter((item) => !item.isDeleted).map((item) => item.timezoneId),
    [value]
  );
  const defaultId =
    value.find((item) => item.isDefault && !item.isDeleted)?.timezoneId ?? '';

  const options = useMemo(() => {
    const names = new Set(catalog);
    return [...catalog, ...selectedIds.filter((id) => !names.has(id))];
  }, [catalog, selectedIds]);

  const timezonesLabel = selectedIds.join(', ');

  const update = (nextIds: string[], nextDefault: string) => {
    const nextDefaultId = nextIds.includes(nextDefault) ? nextDefault : '';
    onChange(toTenantTimezonePayload(nextIds, nextDefaultId, value));
  };

  return (
    <div className={styles.root}>
      <Field
        className={styles.field}
        label={t('timezones', 'Timezones')}
        orientation="horizontal"
        style={FIELD_LABEL_LAYOUT}
        validationState={catalogError ? 'error' : undefined}
        validationMessage={catalogError}
      >
        <Dropdown
          style={{ minWidth: 'auto', width: '100%' }}
          multiselect
          disabled={disabled || catalogFetch.loading || Boolean(catalogError)}
          selectedOptions={selectedIds}
          value={timezonesLabel}
          placeholder={t('selectTimezones', 'Select timezones')}
          onOptionSelect={(_, data) => update(data.selectedOptions, defaultId)}
        >
          {options.map((timezoneId) => (
            <Option key={timezoneId} value={timezoneId}>
              {timezoneId}
            </Option>
          ))}
        </Dropdown>
      </Field>
      <Field
        className={styles.field}
        label={t('defaultTimezone', 'Default timezone')}
        orientation="horizontal"
        style={FIELD_LABEL_LAYOUT}
      >
        <Dropdown
          style={{ minWidth: 'auto', width: '100%' }}
          disabled={disabled || selectedIds.length === 0}
          selectedOptions={defaultId ? [defaultId] : []}
          value={defaultId}
          placeholder={t('selectDefaultTimezone', 'Select default timezone')}
          onOptionSelect={(_, data) =>
            update(selectedIds, data.optionValue ?? '')
          }
        >
          {selectedIds.map((timezoneId) => (
            <Option key={timezoneId} value={timezoneId}>
              {timezoneId}
            </Option>
          ))}
        </Dropdown>
      </Field>
    </div>
  );
};

export default TenantTimezoneFields;
